<template>
	<v-container
		fluid
		class="d-flex align-content-center justify-center flex-wrap background fill-height login-container"
	>
		<v-card
			class="login-card pa-8 pa-sm-10"
			elevation="5"
		>
			<!-- Logo -->
			<div class="d-flex justify-center mb-2">
				<v-img
					class="cassie-login-form-logo"
					max-width="200"
					:src="logoSrc"
				/>
			</div>

			<!-- Subtitle (product descriptor) -->
			<p
				v-if="subtitle"
				class="login-subtitle text-center mb-4"
			>
				{{ subtitle }}
			</p>

			<!-- Main content -->
			<div class="login-form-container">
				<!-- Heading -->
				<h1
					v-if="heading"
					class="login-heading text-center mb-2"
				>
					{{ heading }}
				</h1>

				<!-- Secondary text -->
				<p
					v-if="secondaryText"
					class="login-secondary-text text-center mb-6"
				>
					{{ secondaryText }}
				</p>

				<!-- Legacy portal name support -->
				<v-card-title
					v-if="portalName"
					class="text-h5 justify-center py-4 font-weight-bold"
				>
					{{ portalName }}
				</v-card-title>

				<!-- Legacy form title support -->
				<v-card-subtitle
					v-if="formTitle"
					class="text-h6 pt-3 text-center"
				>
					{{ formTitle }}
				</v-card-subtitle>

				<!-- Form slot -->
				<slot name="form" />
			</div>
		</v-card>
	</v-container>
</template>

<script>
import logoLight from '../assets/Syrenis-Black.svg'
import logoDark from '../assets/Syrenis-red.svg'

const DARK_MODE_STORAGE_KEY = 'cassie-dark-mode'

export default {
	props: {
		portalName: {
			type: String,
			default: ''
		},
		formTitle: {
			type: String,
			default: ''
		},
		subtitle: {
			type: String,
			default: ''
		},
		heading: {
			type: String,
			default: ''
		},
		secondaryText: {
			type: String,
			default: ''
		}
	},
	computed: {
		logoSrc () {
			return this.$vuetify.theme.dark ? logoDark : logoLight
		}
	},
	created () {
		// Initialize dark mode from localStorage (for pages that don't go through main app shell)
		const savedDarkMode = localStorage.getItem(DARK_MODE_STORAGE_KEY)
		if (savedDarkMode !== null) {
			const isDark = savedDarkMode === 'true'
			this.$vuetify.theme.dark = isDark
			if (isDark) {
				document.documentElement.classList.add('dark')
			} else {
				document.documentElement.classList.remove('dark')
			}
		}
	}
}
</script>

<style lang="scss">
.login-container {
  background: var(--global-main-background-color, #f5f5f5);
  width: 100%;
  height: 100vh;
  padding: 0 !important;
  margin: 0 !important;
}

.login-container .v-card {
  background-color: var(--global-card-background, #ffffff);
  width: 420px !important;
  max-width: 100%;
  border-radius: 16px !important;
  /* Aligned with NextJS shadow-lg */
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1) !important;
}

.cassie-login-form-logo {
  flex-shrink: 0;
}

.login-subtitle {
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--global-muted-foreground, #6b7280);
  margin: 0;
}

.login-heading {
  font-size: 16px;
  font-weight: 400;
  letter-spacing: 0;
  color: var(--global-muted-foreground, #6b7280);
  margin: 0;
}

.login-secondary-text {
  font-size: 14px;
  color: var(--global-muted-foreground, #6b7280);
  margin: 0;
}

.login-form-container {
  width: 100%;
}

/* Monochrome icon for login screens */
.login-container .login-icon-muted {
  color: var(--global-muted-foreground, #9ca3af) !important;
}

/* Icon roundel style */
.login-container .login-icon-roundel {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background-color: var(--global-muted, #f3f4f6);
}

.login-container .login-icon-roundel .v-icon {
  color: var(--global-muted-foreground, #6b7280) !important;
  line-height: 1;
  height: auto !important;
}

/* Subtle info text for login screens */
.login-container .login-info-text {
  font-size: 13px;
  color: var(--global-muted-foreground, #6b7280);
  line-height: 1.5;
  margin: 0;
}

/* Dark mode support */
.dark .login-container,
.theme--dark .login-container {
  background: var(--global-main-background-color, #121214);
}

.dark .login-container .v-card,
.theme--dark .login-container .v-card {
  background-color: var(--global-card-background, #1c1c1e) !important;
  /* Dark mode shadow with higher opacity */
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.4), 0 4px 6px -4px rgba(0, 0, 0, 0.3) !important;
}

.dark .login-container .v-card-title,
.dark .login-container .v-card-subtitle,
.theme--dark .login-container .v-card-title,
.theme--dark .login-container .v-card-subtitle {
  color: var(--global-foreground, #f5f5f5) !important;
}

.dark .login-container .login-heading,
.theme--dark .login-container .login-heading {
  color: var(--global-muted-foreground, #9ca3af);
}

.dark .login-container .login-subtitle,
.dark .login-container .login-secondary-text,
.theme--dark .login-container .login-subtitle,
.theme--dark .login-container .login-secondary-text {
  color: var(--global-muted-foreground, #9ca3af);
}

.dark .login-container .login-icon-muted,
.theme--dark .login-container .login-icon-muted {
  color: var(--global-muted-foreground, #6b7280) !important;
}

.dark .login-container .login-icon-roundel,
.theme--dark .login-container .login-icon-roundel {
  background-color: var(--global-muted, #374151);
}

.dark .login-container .login-icon-roundel .v-icon,
.theme--dark .login-container .login-icon-roundel .v-icon {
  color: var(--global-muted-foreground, #9ca3af) !important;
}

.dark .login-container .login-info-text,
.theme--dark .login-container .login-info-text {
  color: var(--global-muted-foreground, #9ca3af);
}

/* Link styling - light mode */
.login-container a {
  color: var(--global-steel-blue, #406A94);
}

.login-container a:hover {
  color: var(--global-prussian-blue, #0B3142);
}

/* Link styling for dark mode - use primary red */
.dark .login-container a,
.theme--dark .login-container a {
  color: var(--global-syrenis-red, #ed303e) !important;
}

.dark .login-container a:hover,
.theme--dark .login-container a:hover {
  color: #f87171 !important; /* Lighter red on hover */
}

/* Body text in dark mode */
.dark .login-container p,
.theme--dark .login-container p {
  color: var(--global-foreground, #e0e0e0);
}

/* Supplementary styling for primary sign-in button (typography, radius, transition) */
.login-container .sign-in-button {
  font-weight: 500;
  letter-spacing: 0.01em;
  border-radius: 8px !important;
  transition: all 0.2s ease;
}

.login-container .sign-in-button:disabled {
  opacity: 0.6;
}

</style>
